<template>
  <div class="document-uploader">
    <div v-if="showDialog" class="fixed inset-0 backdrop-blur-sm bg-gray-800/30 flex items-center justify-center z-[9999] p-4">
      <div class="document-upload-dialog bg-gray-900/95 rounded-lg p-6 max-w-2xl w-full border border-gray-700 shadow-2xl">
        <!-- Header -->
        <div class="mb-6 flex justify-between items-center">
          <h3 class="text-xl font-medium text-white">Add Document to Chat</h3>
          <button 
            @click="closeDialog" 
            class="text-gray-400 hover:text-white transition-colors duration-200"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
        
        <!-- Error message -->
        <div v-if="error" class="bg-red-500/80 text-white p-3 rounded-lg mb-4 error-message">
          The selected paper has an invalid URL
        </div>

        <!-- Upload tabs -->
        <div class="mb-6">
          <!-- Search Type Toggle -->
          <div class="flex justify-center mb-6">
            <div class="bg-white/10 p-1 rounded-lg inline-flex text-sm md:text-base">
              <button 
                @click="setSearchType('direct')"
                class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                :class="searchType === 'direct' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'"
              >
                Direct URL
              </button>
              <button 
                @click="setSearchType('scholar')"
                class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                :class="searchType === 'scholar' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'"
              >
                Scholar
              </button>
              <button 
                @click="setSearchType('arxiv')"
                class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                :class="searchType === 'arxiv' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'"
              >
                arXiv
              </button>
              <button 
                @click="setSearchType('upload')"
                class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                :class="searchType === 'upload' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'"
              >
                Upload
              </button>
            </div>
          </div>

          <!-- Direct URL Input -->
          <div v-if="searchType === 'direct'" class="mb-4">
            <div class="flex items-center mb-2">
              <input 
                v-model="pdfUrl" 
                @input="handleInputChange"
                class="w-full px-4 py-3 text-md rounded-lg text-neutral-700 bg-white border border-gray-300 focus:border-purple-500 focus:ring focus:ring-purple-200 transition duration-200 ease-in-out"
                placeholder="Enter PDF URL (e.g., https://arxiv.org/pdf/2304.12210.pdf)" 
              />
            </div>
            <p class="text-xs text-gray-400 mt-2">
              Enter a direct URL to a PDF file. Must be a valid PDF URL.
            </p>
            <div v-if="urlError" class="text-red-400 text-sm mt-2">
              Invalid URL or cannot access the PDF. Please ensure the URL is correct and publicly accessible.
            </div>
          </div>

          <!-- Scholar Search -->
          <div v-if="searchType === 'scholar'" class="mb-4">
            <div class="relative">
              <input 
                v-model="searchQuery" 
                @input="searchScholar"
                @focus="isSearchFocused = true"
                @blur="handleSearchBlur"
                class="w-full px-4 py-3 text-md rounded-lg text-neutral-700 bg-white border border-gray-300 focus:border-purple-500 focus:ring focus:ring-purple-200 transition duration-200 ease-in-out"
                placeholder="Search for papers on Google Scholar"
              />
              <div v-if="loadingSearch" class="absolute right-3 top-3">
                <i class="pi pi-spin pi-spinner text-gray-400"></i>
              </div>
            </div>
            <div 
              v-if="showSearchResults && searchResults.length" 
              class="mt-2 bg-white border border-gray-300 rounded-lg overflow-hidden shadow-lg max-h-60 overflow-y-auto"
            >
              <div 
                v-for="(paper, index) in searchResults" 
                :key="index"
                @click="selectPaper(paper)"
                class="p-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer"
              >
                <h4 class="text-gray-900 font-medium mb-1">{{ paper.title }}</h4>
                <div class="flex justify-between items-center text-sm">
                  <span class="text-gray-600">{{ formatAuthors(paper.authors) }}</span>
                  <span class="text-gray-600">{{ paper.year }}</span>
                </div>
              </div>
            </div>
            <p v-if="!loadingSearch && searchQuery.length >= 3 && searchResults.length === 0" class="text-gray-400 mt-2 text-sm">
              No results found. Try a different search term.
            </p>
          </div>

          <!-- Arxiv Search -->
          <div v-if="searchType === 'arxiv'" class="mb-4">
            <div class="relative">
              <input 
                v-model="searchQuery" 
                @input="searchArxiv"
                @focus="isSearchFocused = true"
                @blur="handleSearchBlur"
                class="w-full px-4 py-3 text-md rounded-lg text-neutral-700 bg-white border border-gray-300 focus:border-purple-500 focus:ring focus:ring-purple-200 transition duration-200 ease-in-out"
                placeholder="Search for papers on arXiv"
              />
              <div v-if="loadingSearch" class="absolute right-3 top-3">
                <i class="pi pi-spin pi-spinner text-gray-400"></i>
              </div>
            </div>
            <div 
              v-if="showSearchResults && searchResults.length" 
              class="mt-2 bg-white border border-gray-300 rounded-lg overflow-hidden shadow-lg max-h-60 overflow-y-auto"
            >
              <div 
                v-for="(paper, index) in searchResults" 
                :key="index"
                @click="selectPaper(paper)"
                class="p-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer"
              >
                <h4 class="text-gray-900 font-medium mb-1">{{ paper.title }}</h4>
                <div class="flex justify-between items-center text-sm">
                  <span class="text-gray-600">{{ formatAuthors(paper.authors) }}</span>
                </div>
              </div>
            </div>
            <p v-if="!loadingSearch && searchQuery.length >= 3 && searchResults.length === 0" class="text-gray-400 mt-2 text-sm">
              No results found. Try a different search term.
            </p>
          </div>

          <!-- File Upload -->
          <div v-if="searchType === 'upload'" class="mb-4">
            <div 
              class="relative flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg transition-all duration-200"
              :class="[
                dragActive ? 'border-purple-400 bg-purple-400/10' : 'border-gray-500 bg-white/5',
                isFileInputFocused ? 'scale-98' : ''
              ]"
              @dragover.prevent="dragActive = true"
              @dragleave.prevent="dragActive = false"
              @drop.prevent="handleFileDrop($event)"
              @click="$refs.fileInput.click()"
            >
              <input 
                type="file" 
                ref="fileInput" 
                class="hidden" 
                accept="application/pdf"
                @change="(e) => { pdfFile = e.target.files; handleFileUpload(); }"
                @focus="isFileInputFocused = true"
                @blur="handleFileInputBlur"
              >
              
              <div class="flex flex-col items-center space-y-3">
                <div class="p-3 rounded-full" 
                    :class="dragActive ? 'bg-purple-500/20' : 'bg-gray-700/30'">
                  <i class="pi pi-upload text-3xl" :class="dragActive ? 'text-purple-400' : 'text-gray-400'"></i>
                </div>
                <div class="flex flex-col items-center space-y-1 text-center">
                  <span class="text-base font-medium" :class="dragActive ? 'text-purple-400' : 'text-gray-300'">
                    Drop your PDF here or click to browse
                  </span>
                  <span class="text-sm text-gray-400">
                    Support for papers up to 10MB
                  </span>
                </div>
              </div>
              
              <!-- Show selected file info if exists -->
              <div v-if="pdfFile && pdfFile[0]" class="absolute bottom-2 left-2 right-2 mt-2 px-3 py-2 bg-gray-800/80 rounded-lg flex items-center">
                <i class="pi pi-file-pdf text-red-400 mr-2"></i>
                <div class="flex-1 truncate">
                  <p class="text-white truncate">{{ pdfFile[0].name }}</p>
                  <p class="text-xs text-gray-400">{{ formatFileSize(pdfFile[0].size) }}
                    <span v-if="isChunkedUpload" class="ml-1 text-purple-300">(Chunked upload)</span>
                  </p>
                  
                  <!-- Chunked upload progress bar -->
                  <div v-if="isChunkedUpload && loading" class="upload-progress-bar mt-2">
                    <div class="upload-progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
                  </div>
                  <p v-if="isChunkedUpload && loading" class="text-xs text-purple-300 mt-1">
                    Uploading: {{ Math.round(uploadProgress) }}%
                  </p>
                </div>
                <button 
                  type="button" 
                  @click.stop="pdfFile = null"
                  class="text-gray-400 hover:text-white p-1"
                >
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-end space-x-3">
          <button 
            @click="closeDialog" 
            class="px-4 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-lg transition-colors duration-200 focus:outline-none"
          >
            Cancel
          </button>
          <button 
            @click="addDocumentToChat" 
            class="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-purple-600 hover:to-purple-800 rounded-lg transition-all duration-200 focus:outline-none hover:scale-105 flex items-center"
            :disabled="loading"
          >
            <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
            <span>Add Document</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref } from 'vue';
import * as pdfjsLib from 'pdfjs-dist';
// Import tesseract conditionally if OCR is needed
// import { createWorker } from 'tesseract.js';
import { debounce } from 'lodash';
import ChunkedUploader from '../utils/chunkedUploader.js';

// Set the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default {
  name: 'DocumentUploader',
  props: {
    chatSessionId: {
      type: String,
      required: true
    },
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'close', 
    'document-added', 
    'error'
  ],
  data() {
    return {
      searchType: 'direct',
      searchQuery: '',
      pdfUrl: '',
      pdfFile: null,
      pdfData: null,
      error: false,
      urlError: false,
      loading: false,
      loadingSearch: false,
      searchResults: [],
      showSearchResults: false,
      isSearchFocused: false,
      dragActive: false,
      isFileInputFocused: false,
      useURL: true,
      OCRProcessing: false,
      showOCRDialog: false,
      maxFileSize: 100 * 1024 * 1024, // Increased to 100MB
      standardUploadLimit: 10 * 1024 * 1024, // 10MB limit for standard uploads
      isChunkedUpload: false,
      uploadProgress: 0,
    }
  },
  watch: {
    searchType(newValue) {
      if (newValue === 'direct' || newValue === 'scholar' || newValue === 'arxiv') {
        this.useURL = true;
      } else {
        this.useURL = false;
      }
    }
  },
  methods: {
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    handleInputChange() {
      this.error = false;
      this.urlError = false;
    },
    
    setSearchType(type) {
      this.searchType = type;
      this.searchQuery = '';
      this.pdfUrl = '';
      this.searchResults = [];
      this.showSearchResults = false;
      this.error = false;
      this.urlError = false;
    },
    
    formatAuthors(authors) {
      if (!authors) return '';
      const authorList = Array.isArray(authors) ? authors : [authors];
      return authorList.slice(0, 3).join(', ') + (authorList.length > 3 ? '...' : '');
    },
    
    searchScholar: debounce(async function() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.searchResults = [];
        this.showSearchResults = false;
        return;
      }

      try {
        this.loadingSearch = true;
        const pbUser = this.$cookies.get('pb_user');
        const pbToken = this.$cookies.get('pb_token');
        
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_URL}/search-scholar?q=${encodeURIComponent(this.searchQuery)}`,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );

        if (response.data && response.data.results) {
          this.searchResults = response.data.results;
          this.showSearchResults = true;
        }
      } catch (error) {
        console.error('Error fetching Google Scholar results:', error);
        this.searchResults = [];
      } finally {
        this.loadingSearch = false;
      }
    }, 500),

    searchArxiv: debounce(async function() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.searchResults = [];
        this.showSearchResults = false;
        return;
      }

      try {
        this.loadingSearch = true;
        const response = await axios.get(`https://export.arxiv.org/api/query?search_query=all:${encodeURIComponent(this.searchQuery)}&start=0&max_results=10`);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response.data, "text/xml");
        const entries = xmlDoc.getElementsByTagName("entry");

        this.searchResults = Array.from(entries).map(entry => ({
          title: entry.getElementsByTagName("title")[0]?.textContent || '',
          authors: Array.from(entry.getElementsByTagName("author")).map(author =>
            author.getElementsByTagName("name")[0]?.textContent || ''
          ),
          id: entry.getElementsByTagName("id")[0]?.textContent || '',
          pdfLink: entry.getElementsByTagName("link")[1]?.getAttribute("href") || '',
        }));

        this.showSearchResults = true;
      } catch (error) {
        console.error('Error fetching arxiv results:', error);
        this.searchResults = [];
      } finally {
        this.loadingSearch = false;
      }
    }, 500),
    
    selectPaper(paper) {
      if (this.searchType === 'arxiv') {
        this.pdfUrl = paper.pdfLink.replace('http://', 'https://');
        this.pdfUrl = this.pdfUrl.replace('arxiv.org/abs/', 'arxiv.org/pdf/');
      } else {
        this.pdfUrl = paper.url;
      }
      this.searchQuery = paper.title;
      this.showSearchResults = false;
    },
    
    async handleFileUpload() {
      if (!this.pdfFile || !this.pdfFile[0]) {
        return;
      }

      const file = this.pdfFile[0];

      if (file.size > this.maxFileSize) {
        this.error = true;
        this.$nextTick(() => {
          document.querySelector('.error-message').textContent =
            `File size exceeds the maximum limit of ${this.maxFileSize / (1024 * 1024)}MB. Please choose a smaller file.`;
        });
        return;
      }

      this.isFileInputFocused = true;
      this.pdfUrl = file.name;
      
      // All files will use chunked upload - flag if it's a "large" file for UI purposes
      this.isChunkedUpload = file.size > this.standardUploadLimit;
    },
    
    handleFileDrop(e) {
      this.dragActive = false;
      const files = e.dataTransfer.files;
      if (files.length && files[0].type === 'application/pdf') {
        this.pdfFile = files;
        this.isFileInputFocused = true;
        this.handleFileUpload();
      }
    },
    
    handleFileInputBlur() {
      // Only unfocus if no file is selected
      if (!this.pdfFile || !this.pdfFile[0]) {
        this.isFileInputFocused = false;
      }
    },
    
    handleSearchBlur() {
      // Only unfocus if there's no search query and no results showing
      if (!this.searchQuery && !this.showSearchResults) {
        this.isSearchFocused = false;
      }
    },
    
    checkFileSize(size) {
      if (size > this.maxFileSize) {
        this.error = true;
        return false;
      }
      return true;
    },
    
    closeDialog() {
      console.log('DocumentUploader: Closing dialog');
      this.$emit('close');
    },
    
    async getPDFData() {
      if (this.pdfData !== null) {
        return this.pdfData;
      }
      // Fetch the PDF data from the URL, and handle any errors
      return axios.get(this.pdfUrl, { responseType: 'arraybuffer' })
        .then((response) => {
          const base64Data = btoa(
            new Uint8Array(response.data).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          this.pdfData = base64Data;
          return base64Data;
        })
    },
    
    async extractTextFromPDF(pdfBase64) {
      const uint8Array = new Uint8Array(atob(pdfBase64).split('').map(char => char.charCodeAt(0)));
      const pdf = await pdfjsLib.getDocument({ data: uint8Array }).promise;
      let allText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        allText += pageText + '\n\n';
      }

      return allText;
    },

    async checkPDFHasText(pdfBase64) {
      const text = await this.extractTextFromPDF(pdfBase64);
      return text.trim().length > 0;
    },
    
    async addDocumentToChat() {
      console.log('DocumentUploader: Adding document to chat session with ID:', this.chatSessionId);  // Debug the chatSessionId
      
      if (this.pdfFile === null && this.pdfUrl === '') {
        console.log('DocumentUploader: No PDF file or URL provided');
        this.error = true;
        this.$nextTick(() => {
          document.querySelector('.error-message').textContent = 'The selected paper has an invalid URL' 
        });
        return;
      }

      let alteredPdfUrl = this.pdfUrl.replaceAll(':', '_');
      alteredPdfUrl = alteredPdfUrl.replaceAll('/', '_');
      console.log('DocumentUploader: Using altered PDF URL:', alteredPdfUrl);
      this.loading = true;

      try {
        let jobId;
        
        if (this.useURL) {
          console.log('DocumentUploader: Uploading document via URL');
          try {
            // Upload the document which now returns a job_id instead of document_id
            const response = await axios.post(
              `${process.env.VUE_APP_BASE_URL}/add-document-to-chat?chat_session_id=${this.chatSessionId}&document_id=${alteredPdfUrl}`,
              { pdf_url: this.pdfUrl },
              {
                headers: {
                  'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                  'User': this.$cookies.get('pb_user'),
                  'Content-Type': 'application/json'
                }
              }
            );

            console.log('DocumentUploader: Server response:', response.data);
            
            // Check if we have a job_id for async processing
            if (response.data && response.data.job_id) {
              jobId = response.data.job_id;
              console.log('DocumentUploader: Job ID received:', jobId);
            } else {
              throw new Error('No job ID received from server');
            }
          } catch (error) {
            console.error('DocumentUploader: URL access failed:', error);
            this.urlError = true;
            this.error = false; // Hide the general error message
            throw new Error('URL access failed');
          }
        } else if (this.pdfFile && this.pdfFile[0]) {
          // Always use chunked upload for all files
          try {
            const file = this.pdfFile[0];
            const uploader = new ChunkedUploader({
              baseUrl: process.env.VUE_APP_BASE_URL,
              token: this.$cookies.get('pb_token'),
              user: this.$cookies.get('pb_user'),
              // Use file size as chunk size for small files (effectively a single chunk)
              chunkSize: file.size > this.standardUploadLimit ? 5 * 1024 * 1024 : file.size,
              onProgress: (progress) => {
                this.uploadProgress = progress;
              }
            });
            
            // Upload the file in chunks
            const result = await uploader.uploadFile(file);
            
            if (result && result.doc_id) {
              // Now add the document to the chat session
              const addResponse = await axios.post(
                `${process.env.VUE_APP_BASE_URL}/add-document-to-chat?chat_session_id=${this.chatSessionId}&document_id=${result.doc_id}&existing_document_id=${result.doc_id}`,
                {},
                {
                  headers: {
                    'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                    'User': this.$cookies.get('pb_user'),
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              if (addResponse.data && addResponse.data.job_id) {
                jobId = addResponse.data.job_id;
              }
            } else {
              throw new Error('Document processing failed');
            }
          } catch (error) {
            console.error('Upload error:', error);
            this.error = true;
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent = 
                'Failed to upload file. Please try again.';
            });
            throw error;
          }
        } else {
          console.error('DocumentUploader: No file or URL provided');
          this.error = true;
          this.$nextTick(() => {
            document.querySelector('.error-message').textContent = 
              'No file selected or URL provided.';
          });
          throw new Error('No file or URL provided');
        }
        
        // If we got here, document upload was initiated successfully
        // Emit only the job ID to the parent for polling
        this.$emit('document-added', null, jobId);
        
        this.closeDialog();
        
      } catch (error) {
        console.error('DocumentUploader Error:', error);
        if (!this.urlError) { // Only show the general error if urlError isn't shown
          this.error = true;
          if (error.response && error.response.status === 413) {
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent =
                'Error uploading file. Please try again.';
            });
          }
        }
        
        this.$emit('error', 'Failed to add document. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.document-uploader {
  width: 100%;
}

.document-upload-dialog {
  max-width: 650px;
  width: 100%;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.scale-98 {
  transform: scale(0.98);
}

.upload-progress-bar {
  width: 100%;
  height: 6px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.upload-progress-fill {
  height: 100%;
  background-color: #8B5CF6;
  transition: width 0.3s ease;
}
</style> 